/**
 * Whether the scope carries context keys the connector cannot translate into its
 * API filters. Such a connector must contribute NO results — ignoring the scope
 * would present unfiltered matches as if they were scoped.
 *
 * @param scope - The active scope.
 * @param supportedKeys - The `context` keys the connector can honor.
 * @returns {boolean} True when at least one scope key is not supported.
 */
export function hasUnsupportedScope(
    scope: SearchScope,
    supportedKeys: readonly string[],
): boolean {
    return Object.keys(scope?.context ?? {}).some((key) => !supportedKeys.includes(key));
}

/**
 * The scope a search runs in. Connectors receive it with every query and decide
 * themselves which parts they honor (e.g. an object/property filter chip).
 */
export interface SearchScope {
    /**
     * Named scope chips shown above the results, e.g. `{ objektId: '…' }`.
     * Connectors translate the entries into their own API filters.
     */
    readonly context?: Readonly<Record<string, unknown>>;

    /**
     * Human-readable labels for the chips, keyed like `context`,
     * e.g. `{ objektId: 'Objekt: GdWE Lindenstraße 12' }`.
     */
    readonly labels?: Readonly<Record<string, string>>;
}
